import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Spin } from "antd";
import { blogAPI } from "../services/api";
import { titleToSlug } from "../utils/blogUtils";
import "../styles/home-blogs.css";

export default function HomeBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    if (blogs.length > 3) {
      startAutoScroll();
      return () => stopAutoScroll();
    }
  }, [blogs.length]);

  useEffect(() => {
    if (scrollContainerRef.current && blogs.length > 3) {
      const container = scrollContainerRef.current;
      const firstCard = container.querySelector('.home-blog-card');
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 32; // 2rem gap
        const scrollPosition = currentIndex * (cardWidth + gap);
        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [currentIndex, blogs.length]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      // Fetch published blogs, limit to 6 for home page
      const response = await blogAPI.getAll({ 
        status: 'published',
        limit: 6 
      });
      setBlogs(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return "";
    }
  };

  const getImageUrl = (blog) => {
    if (blog.featured_image) {
      if (blog.featured_image.startsWith('/uploads')) {
        const backendUrl = process.env.REACT_APP_ADMIN_API_URL || 'https://ampsweb.saassrs.com';
        return `${backendUrl}${blog.featured_image}`;
      }
      return blog.featured_image;
    }
    return "/images/backimg1.jpg"; // Default image
  };

  const startAutoScroll = () => {
    stopAutoScroll();
    autoScrollIntervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const maxIndex = Math.max(0, blogs.length - 3);
        if (prevIndex >= maxIndex) {
          return 0; // Loop back to start
        }
        return prevIndex + 1;
      });
    }, 4000); // Auto-scroll every 4 seconds
  };

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };


  const handleMouseEnter = () => {
    stopAutoScroll();
  };

  const handleMouseLeave = () => {
    startAutoScroll();
  };

  if (loading) {
    return (
      <section id="home-blogs" className="home-blogs-section scroll-mt-24">
        <div className="container home-blogs-container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin size="large" />
          </div>
        </div>
      </section>
    );
  }

  if (blogs.length === 0) {
    return null; // Don't show section if no blogs
  }

  return (
    <section id="home-blogs" className="home-blogs-section scroll-mt-24">
      <div className="container home-blogs-container">
        <div className="text-center mb-4 mb-md-5">
          <h2 className="home-blogs-heading">
            Latest <span>Blogs</span>
          </h2>
          <p className="home-blogs-subheading">
            Read our latest articles and insights
          </p>
        </div>

        <div className="home-blogs-wrapper">
          <div
            className="home-blogs-grid"
            ref={scrollContainerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {blogs.map((blog) => {
              // Generate slug from title if slug doesn't exist
              const slug = blog.slug || titleToSlug(blog.title);
              return (
                <Link
                  key={blog.id}
                  to={`/blogs/${slug}`}
                  className="home-blog-card"
                >
                  <div className="home-blog-card__image">
                    <img 
                      src={getImageUrl(blog)} 
                      alt={blog.title}
                      onError={(e) => {
                        e.target.src = "/images/backimg1.jpg";
                      }}
                    />
                  </div>
                  <div className="home-blog-card__content">
                    <div className="home-blog-card__meta">
                      {blog.author_name && (
                        <span className="home-blog-card__author">{blog.author_name}</span>
                      )}
                      {blog.created_at && (
                        <span className="home-blog-card__date">{formatDate(blog.created_at)}</span>
                      )}
                    </div>
                    <h3 className="home-blog-card__title">{blog.title}</h3>
                    {blog.excerpt && (
                      <p className="home-blog-card__excerpt">{blog.excerpt}</p>
                    )}
                    <span className="home-blog-card__read-more">Read More →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="text-center mt-4 mt-md-5">
          <Link to="/blogs" className="home-blogs-view-all">
            View All Blogs →
          </Link>
        </div>
      </div>
    </section>
  );
}

